import normalizeParentThreadId from '@/lib/threads/normalizeParentThreadId.js';
import normalizeProjectId from '@/lib/normalizeProjectId.js';

const DEFAULT_PROJECT_SECTION_ID = '__unassigned__';
const DEFAULT_PROJECT_SECTION_LABEL = 'No project';
const HOME_PROJECT_ID = 'project-home';

const buildThreadSections = ({ threadItems, projectMetaById, activeProjectId }) => {
  const getProjectSectionLabel = (sectionId) => {
    if (sectionId === DEFAULT_PROJECT_SECTION_ID) {
      return DEFAULT_PROJECT_SECTION_LABEL;
    }

    return String(projectMetaById?.[sectionId]?.name || 'Project');
  };
  const getProjectSectionSortLabel = (sectionId) => {
    if (sectionId === HOME_PROJECT_ID) {
      return 'Home';
    }

    if (sectionId === DEFAULT_PROJECT_SECTION_ID) {
      return DEFAULT_PROJECT_SECTION_LABEL;
    }

    return String(projectMetaById?.[sectionId]?.name || sectionId || 'Project');
  };
  const getSortedKnownProjectSectionIds = () => {
    const knownProjectIds = Object.values(projectMetaById || {})
      .map((project) => normalizeProjectId(project?.id || project?.projectId))
      .filter(Boolean);

    return knownProjectIds.sort((leftProjectId, rightProjectId) => {
      if (leftProjectId === HOME_PROJECT_ID && rightProjectId !== HOME_PROJECT_ID) {
        return -1;
      }

      if (rightProjectId === HOME_PROJECT_ID && leftProjectId !== HOME_PROJECT_ID) {
        return 1;
      }

      const byLabel = getProjectSectionSortLabel(leftProjectId).localeCompare(getProjectSectionSortLabel(rightProjectId), undefined, {
        sensitivity: 'base'
      });

      if (byLabel !== 0) {
        return byLabel;
      }

      return String(leftProjectId).localeCompare(String(rightProjectId));
    });
  };
  const resolveActiveProjectId = () => {
    const normalizedActiveProjectId = normalizeProjectId(activeProjectId);

    if (!normalizedActiveProjectId) {
      return null;
    }

    const hasExplicitProject = Boolean(projectMetaById?.[normalizedActiveProjectId]);
    const hasThreadInProject = (threadItems || []).some((item) => item?.projectId === normalizedActiveProjectId);

    if (hasExplicitProject || hasThreadInProject) {
      return normalizedActiveProjectId;
    }

    return null;
  };
  const buildNestedRootThreadItems = () => {
    const orderedItems = (threadItems || []).map((item) => {
      return {
        ...item,
        childThreadItems: []
      };
    });
    const itemById = {};

    for (const item of orderedItems) {
      const itemId = String(item?.id || '').trim();

      if (!itemId) {
        continue;
      }

      itemById[itemId] = item;
    }

    const rootItems = [];

    for (const item of orderedItems) {
      const parentThreadId = normalizeParentThreadId(item?.parentThreadId);

      if (!parentThreadId) {
        rootItems.push(item);
        continue;
      }

      const parentItem = itemById[parentThreadId] || null;

      if (!parentItem || parentItem?.id === item?.id) {
        continue;
      }

      parentItem.childThreadItems = [...(parentItem.childThreadItems || []), item];
    }

    return rootItems;
  };
  const resolvedActiveProjectId = resolveActiveProjectId();
  const knownProjectSectionIds = getSortedKnownProjectSectionIds();
  const knownProjectSectionIdSet = new Set(knownProjectSectionIds);
  const sectionById = {};
  const ensureSection = (sectionId) => {
    if (sectionById[sectionId]) {
      return sectionById[sectionId];
    }

    const section = {
      id: sectionId,
      projectId: sectionId === DEFAULT_PROJECT_SECTION_ID ? null : sectionId,
      label: getProjectSectionLabel(sectionId),
      isActive: Boolean(resolvedActiveProjectId && sectionId === resolvedActiveProjectId),
      threadItems: []
    };

    sectionById[sectionId] = section;
    return section;
  };

  for (const knownProjectSectionId of knownProjectSectionIds) {
    ensureSection(knownProjectSectionId);
  }

  for (const item of buildNestedRootThreadItems()) {
    const sectionId = item?.projectId || DEFAULT_PROJECT_SECTION_ID;
    ensureSection(sectionId).threadItems.push(item);
  }

  const dynamicSectionIds = Object.keys(sectionById).filter((sectionId) => !knownProjectSectionIdSet.has(sectionId));
  dynamicSectionIds.sort((leftSectionId, rightSectionId) => {
    if (leftSectionId === HOME_PROJECT_ID && rightSectionId !== HOME_PROJECT_ID) {
      return -1;
    }

    if (rightSectionId === HOME_PROJECT_ID && leftSectionId !== HOME_PROJECT_ID) {
      return 1;
    }

    if (leftSectionId === DEFAULT_PROJECT_SECTION_ID && rightSectionId !== DEFAULT_PROJECT_SECTION_ID) {
      return 1;
    }

    if (rightSectionId === DEFAULT_PROJECT_SECTION_ID && leftSectionId !== DEFAULT_PROJECT_SECTION_ID) {
      return -1;
    }

    const byLabel = getProjectSectionSortLabel(leftSectionId).localeCompare(getProjectSectionSortLabel(rightSectionId), undefined, {
      sensitivity: 'base'
    });

    if (byLabel !== 0) {
      return byLabel;
    }

    return String(leftSectionId).localeCompare(String(rightSectionId));
  });

  return [...knownProjectSectionIds, ...dynamicSectionIds].map((sectionId) => sectionById[sectionId]).filter(Boolean);
};

export default buildThreadSections;
